<template lang="pug">
.uc-add-screen.z-100.auto-alpha-0.flex.justify-center(:class="[isEditModeActive && showAddScreen ? 'auto-alpha-1' : '']")
  //- add section
  button.add-page-button.fixed.bottom-6.inline-block.text-sm.dark_text-white.select-none.py-2.px-4.rounded-full.flex.items-center.justify-between.gap-1.group.focus-visible_ring-8(
    class="dark_bg-black/70 bg-white/70"
    ref="elRef"
    v-if="props.type === 'section' && isEditModeActive && showAddScreen"
    @click="toggleDrawer"
  )
    .add-screen-content.ms-1.opacity-80.group-hover_opacity-100.transition
      uc-text(
        text="Add page"
      )
    | #{ ' ' }
    .add-screen-icon.transition-colors
      uc-icon.text-2xl.text-indigo-700(
        icon="theme:plus-circle"
      )

.add-drawer__underlay.fixed.inset-0.h-screen.w-screen.auto-alpha-1.bg-gray-800.bg-opacity-70.z-10(
  v-if="drawerOpen"
  @click="toggleDrawer"
)
.add-drawer-container.w-full.flex.justify-center.items-center.primary-indigo
  .add-drawer.z-100.fixed.max-h-full.overflow-y-auto.bottom-4.object-center.rounded-lg(
    :class="twMerge('w-[90vw] min-h-[65vh]', drawerOpen ? 'translate-y-0 auto-alpha-1 visible bg-secondary-900' : 'auto-alpha-0 translate-y-full invisible')"
    v-if="isEditModeActive"
    ref="addDrawerRef"
  )
    UseFocusTrap.flex.flex-col.h-full(v-if="drawerOpen")
      .add-header.flex.flex-row.items-start.justify-between.p-4.m-4.min-h-4()
        .heading.px-4.text-2xl.font-bold.text-indigo-600
          uc-text(
            type="h2"
            text="Add new page"
          )
        .add-drawer-close
          uc-button.btn.btn-icon.cursor-auto.bg-white.hover_bg-secondary-900.font-bold.w-8.h-8.z-100.rounded-full.focus-visible_ring-8(
                @click="toggleDrawer"
                type="button"
                aria-label="Close"
            )
            uc-icon(icon="theme:menu-close")  
      form.add-components.flex.flex-col.justify-between.items-center.px-8.w-full
        .add-drawer-header.pb-4.px-4
          uc-text(
            text="Select a page layout"
          )
        .add-component-list.px-4.grid.grid-cols-1.lg_grid-cols-4.gap-4
          .add-component.p-2.justify-between.items-center.rounded-lg(
            v-for="(component, i) in computedComponents"
            :class="[ isSelected(component.template) ? 'bg-gray-300' : 'bg-white' ]"
          )
            input.peer.sr-only.focus-visible_ring-4(
              :checked="isSelected(component.template)"
              :id="component.template"
              :value="component.template"
              @change="changeHandler"
              uc-word-skip=""
              type="radio"
            )
            label.component-option.flex.flex-col.items-start.h-full.justify-stretch.transition.p-1.cursor-pointer.ripple(
              :for="component.template"
            )
              .ripple__wave
              .component-image(
                :data-test="component.img"
              )
                uc-asset(
                  :src="component.img" 
                  :size="28"
                )
              .component-description.flex.items-start.pt-2.px-1
                uc-text(
                  :text="component.displayName"
                )
        .component-add.group.w-full.flex.flex-col.items-center.justify-end.pb-8(
          :class="[ !selected ? 'pt-6' : 'pt-12' ]"
        )
          //- select template message
          p.transition.opacity-0.group-hover_opacity-100.italic.duration-500.group-hover_delay-1000.dark_text-gray-200(
            uc-word-skip=""
            v-if="!selected"
          ) 
            uc-text(
              text="Select a page layout"
            )
          button.mt-1.py-1.px-6.text-white.rounded-lg.focus-visible_ring-8(
            @click="addPage"
            type="button"
            aria-label="Create page"
            :disabled="!selected"
            :class="[ !selected ? 'opacity-60 bg-gray-600': 'opacity-100 bg-indigo-500 hover_bg-indigo-900' ]"
          )
            uc-text(
              text="Create page"
            )

</template>
<script setup lang="ts">
import {
  computed,
  inject,
  ref,
  unref,
  nextTick,
  onMounted,
  watchEffect
} from 'vue';
import { useEventListener, onKeyDown, onClickOutside } from '@vueuse/core';
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component';
import { twMerge } from '../lib/mergeStyles';
import useContent from '../stores/useContent';
import useSuspend from '../lib/useSuspend';
import evaluateCondition from '../lib/evaluateCondition';
import templateStarters from '../lib/templateStarters.json';
import { cloneDeep } from 'lodash-es';
import getOrderedPatches from '../lib/getOrderedPatches';
import getEditorWindow from '../lib/getEditorWindow';

const suspend = useSuspend();
const currentLocation = inject('currentLocation');
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const goToSection = inject('goToSection');
const props = defineProps<{
  unit?: Page;
  type: string;
  id?: string;
}>();

const content = useContent();
const elRef = ref<HTMLElement>();
const drawerOpen = ref(false);
const addDrawerRef = ref<HTMLElement>();
const currentScreen = ref<HTMLElement>();
const currentScreenId = ref('');
const selected = ref('');
const insertRegex = /^(<|>) *#?(\S+)/;

// yaml to insert screen after currentScreenId.value
const basePatchYaml = '';

const toggleDrawer = () => {
  drawerOpen.value = !drawerOpen.value;
};

const closeDrawer = () => {
  drawerOpen.value = false;
  nextTick(() => {
    selected.value = '';
  });
};

const computedComponents = computed(() => {
  return Object.keys(templateStarters).map(key => {
    return {
      template: key,
      img: templateStarters[key].img,
      displayName: templateStarters[key].displayName
    };
  });
});

const firstScreenId = computed(() => {
  return currentPageContent.value?.screens[0]?.id;
});

const showAddScreen = computed(() => {
  if (!isEditModeActive.value || currentLocation.value === 'audio-popup')
    return false;

  const currentPageScreenCount = currentPageContent.value?.screens?.length;
  const isFinalPage =
    currentScreenId.value ===
    currentPageContent.value?.screens[currentPageScreenCount - 1]?.id;
  const isAssessment = currentScreen.value?.querySelector(
    '.screen--assessment'
  );
  const isBank = currentScreen.value?.querySelector('.screen--bank');
  const isQuiz = currentScreen.value?.querySelector('.screen--quiz');

  let shouldShow = true;

  if (isAssessment || isBank || isQuiz || isFinalPage) {
    shouldShow = false;
  }

  return shouldShow;
});

const currentPageContent = computed(() => {
  return content.compiled?.pages?.filter(
    page => page.id === currentLocation.value
  )[0];
});

onMounted(() => {
  useEventListener(window, 'scrollsectionentered', e => {
    currentScreenId.value = e.target?.dataset?.bankId || e.target.id;
    currentScreen.value = e.target as HTMLElement;
  });
});

watchEffect(() => {
  const screenEl = document.querySelector('.section--active');
  if (screenEl) {
    currentScreenId.value = screenEl?.dataset?.bankId || screenEl?.id;
    currentScreen.value = screenEl as HTMLElement;
  }
});

const isSelected = (template: string) => {
  return selected.value === template;
};

const addPage = () => {
  if (!selected.value) return;
  const parentId = currentScreenId.value || firstScreenId.value;
  // get the starter yaml from templateStarters
  const starterJson = Object.assign(
    {},
    templateStarters[selected.value]?.yamlprops
  );
  const id = generateUniqueId(selected.value);
  const editPath = `> #${parentId}`;
  let existingPatch;
  let newEditPath;

  if (!starterJson) {
    console.error('No starter yaml found for ' + selected.value);
    return;
  }

  // clone the patches object so we can manipulate it with changes without triggering a reactivity update
  let newPatches = cloneDeep(content.compiled?.patches) || {};

  // editPath patch already exists in patches, create new patch and place after new id
  if (newPatches[editPath]) {
    existingPatch = Object.assign({}, newPatches[editPath][0]);
    newEditPath = `> #${id}`;

    // update the cloned patches object with the moved patch
    Object.assign(newPatches, {
      [newEditPath]: [existingPatch]
    });
  }

  // apply parent page properties to the new screen (bg, styles etc.)
  Object.assign(starterJson, getParentLayoutProps(parentId));

  // update the starterYaml json id value with the new id
  starterJson.id = id;

  // TODO: add sub screen ID's in a better way. Perhaps automatically like in mod-content src/lib/processYAML.js ln 294
  // Preemptively add sub screen ID's for image-left and image-right
  if (selected.value === 'image-right' || selected.value === 'image-left') {
    starterJson.left[0].id = `${id}-left`;
    starterJson.right[0].id = `${id}-right`;
  } else if (selected.value === 'question-survey-confirm') {
    starterJson.options.forEach((option, index) => {
      option.id = `${id}-option-${index}`;
    });
  }

  // add the new screen to the patches object
  Object.assign(newPatches, {
    [editPath]: [starterJson]
  });

  // remove all screenIndex props from advanced patches
  Object.keys(newPatches).forEach(key => {
    if (insertRegex.test(key)) {
      delete newPatches[key][0].screenIndex;
    }
  });

  // order the newPatches
  newPatches = getOrderedPatches(newPatches);

  // now we update the patches in the content, triggering applyPatches in compiled.
  // Resolve the authoring host lazily (see getEditorWindow) — its presence is the
  // authoring gate. Resolved at click time, not import time, so it sees the host's
  // runtime-assigned `acceptPatchesFromPreview` handler.
  const hostWindow = getEditorWindow('acceptPatchesFromPreview');
  console.log('[ADD-PAGE save]', performance.now().toFixed(0), {
    id,
    patchKey: editPath,
    patchKeys: Object.keys(newPatches),
    authoring: !!hostWindow?.acceptPatchesFromPreview
  });
  if (hostWindow?.acceptPatchesFromPreview) {
    // Authoring: update the local store synchronously so the new page renders in
    // the preview immediately (and goToSection below can find it), then persist
    // the patches into the Monaco YAML draft — the draft's source of truth. The
    // default path (updateValue -> commit -> updateCustomClientAPI) only saves to
    // the draft when clientEditMode is set, which it isn't in authoring.
    content.setEditValue('patches', newPatches);
    hostWindow.acceptPatchesFromPreview(JSON.stringify(newPatches));
  } else {
    console.warn('[ADD-PAGE save] no authoring host — store-only fallback', {
      id,
      patchKey: editPath
    });
    content.updateValue('patches', newPatches);
  }
  nextTick(async () => {
    await window.dispatchEvent(new CustomEvent('tagschanged'));
    closeDrawer();
  });
  nextTick(async () => {
    await goToSection.value(currentLocation.value, id, { duration: 1 });
  });
};

function getParentLayoutProps(parentId) {
  const inheritedLayoutProps = ['dark', 'style', 'fullscreen', 'bg', 'no-fade'];
  const parentScreen =
    currentPageContent.value?.screens?.filter(
      screen => screen.id === parentId
    )[0] || {};
  const layoutProps = {};
  inheritedLayoutProps.forEach(prop => {
    if (parentScreen && parentScreen[prop]) {
      // For backgrounds only take the 1st and add an overlay
      if (prop === 'bg') {
        const firstBg = parentScreen[prop]?.split(' ')[0];
        const newBg = parentScreen.dark
          ? `${firstBg} uploads/black_80.png`
          : `${firstBg} uploads/white_80.png`;
        Object.assign(layoutProps, { [prop]: newBg });
      } else {
        Object.assign(layoutProps, { [prop]: parentScreen[prop] });
      }
    }
  });
  return layoutProps;
}

function changeHandler(event: Event & { target: HTMLInputElement }) {
  if (!event.target.checked || !event.target.value) return;
  selected.value = event.target.value;
}

// generates a unique id and checks content to see if it is already in use
const generateUniqueId = (template: string) => {
  const randomId = `${template}-${Math.random().toString(36).substr(2, 9)}`;
  const isIdInUse = evaluateCondition(randomId, content.compiled);
  const realId = isIdInUse ? generateUniqueId() : randomId;
  return realId;
};

onClickOutside(addDrawerRef, closeDrawer);
onKeyDown('Escape', closeDrawer);
</script>
<style scoped>
.add-drawer {
  background: #eff1fe;
}

.add-screen-icon {
  color: rgb(var(--color-blue-700));
}
</style>
